/**
 * Cross-Tab Auth Bridge
 *
 * Provides a minimal, same-origin auth state bridge between browser tabs using BroadcastChannel.
 *
 * SECURITY CONTRACT:
 * - Token is NEVER stored in localStorage (persistent exposure risk).
 * - Token is only copied into sessionStorage of the requesting tab after successful bridge.
 * - Only same-origin tabs can communicate (BroadcastChannel enforcement).
 *
 * BEHAVIORAL CONTRACT:
 * - Requesting tab sends AUTH_REQUEST and waits for AUTH_RESPONSE with timeout.
 * - Authenticated tab responds with token if it has a valid session token.
 * - Requesting tab stores received token in sessionStorage via authStorage adapter.
 * - Bridge is one-time: requesting tab closes channel after response or timeout.
 *
 * PRECONDITION: Browser supports BroadcastChannel API (all modern browsers).
 * POSTCONDITION: New tab has token in sessionStorage after successful bridge.
 * SIDE EFFECTS: Creates short-lived BroadcastChannel instance for request/response.
 * THROWS: TimeoutError if no authenticated tab responds within timeout window.
 *
 * TODO [FUTURE OPTION C]: Replace with backend SPA reissue/refresh path.
 * This frontend-only bridge is a stopgap. The ideal long-term solution is a dedicated
 * backend endpoint (e.g., POST /auth/session_reissue) that validates the PHP session
 * cookie and returns a fresh bearer token. This would allow new tabs to restore auth
 * state without cross-tab communication and without storing tokens in localStorage.
 *
 * @module services/crossTabAuthBridge
 */

import authStorage from "./authStorage"

const BRIDGE_CHANNEL = "plastinka-auth-bridge"
const REQUEST_TIMEOUT = 3000 // 3 seconds is generous for local cross-tab communication

const MESSAGE_TYPES = {
  AUTH_REQUEST: "auth_request",
  AUTH_RESPONSE: "auth_response",
}

/**
 * Request auth token from another authenticated tab via BroadcastChannel.
 *
 * @precondition Calling tab has no token in sessionStorage (new tab scenario).
 * @postcondition Token is stored in sessionStorage via authStorage.setToken() on successful bridge.
 * @sideeffects Creates BroadcastChannel, sends AUTH_REQUEST, waits for AUTH_RESPONSE, then closes channel.
 * @throws {Error} If timeout expires without receiving a response from an authenticated tab.
 * @returns {Promise<boolean>} true if token was received and stored, false if no authenticated tab responded.
 */
export async function requestAuthTokenFromOtherTab() {
  return new Promise((resolve) => {
    // Early exit if browser doesn't support BroadcastChannel
    if (typeof BroadcastChannel === "undefined") {
      console.warn("[CrossTabAuthBridge] BroadcastChannel not supported")
      resolve(false)
      return
    }

    const channel = new BroadcastChannel(BRIDGE_CHANNEL)
    let resolved = false
    let timeoutId = null

    // Cleanup handler to avoid double-resolve
    const cleanup = () => {
      if (resolved) return
      resolved = true

      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }

      try {
        channel.close()
      } catch (error) {
        console.warn("[CrossTabAuthBridge] Error closing channel:", error)
      }
    }

    // Handle response from authenticated tab
    channel.onmessage = (event) => {
      const { type, token } = event.data || {}

      if (type === MESSAGE_TYPES.AUTH_RESPONSE && token) {
        // Store token in sessionStorage via authStorage adapter
        authStorage.setToken(token)

        cleanup()
        resolve(true)
      }
    }

    // Set timeout to avoid hanging indefinitely
    timeoutId = setTimeout(() => {
      console.warn(
        "[CrossTabAuthBridge] No authenticated tab responded within timeout"
      )
      cleanup()
      resolve(false)
    }, REQUEST_TIMEOUT)

    // Send request to any listening authenticated tab
    try {
      channel.postMessage({
        type: MESSAGE_TYPES.AUTH_REQUEST,
        timestamp: Date.now(),
      })
    } catch (error) {
      console.error("[CrossTabAuthBridge] Failed to send auth request:", error)
      cleanup()
      resolve(false)
    }
  })
}

/**
 * Listen for auth requests from other tabs and respond with current token if authenticated.
 *
 * @precondition Calling tab has a valid token in sessionStorage.
 * @postcondition Responding tab sends token to requesting tab via BroadcastChannel.
 * @sideeffects Creates long-lived BroadcastChannel listener for the tab's lifetime.
 * @returns {{ stop: function }} Function to stop listening and close the channel.
 */
export function listenForAuthRequests() {
  // Early exit if browser doesn't support BroadcastChannel
  if (typeof BroadcastChannel === "undefined") {
    console.warn("[CrossTabAuthBridge] BroadcastChannel not supported")
    return { stop: () => {} }
  }

  const channel = new BroadcastChannel(BRIDGE_CHANNEL)

  channel.onmessage = (event) => {
    const { type } = event.data || {}

    if (type === MESSAGE_TYPES.AUTH_REQUEST) {
      // Check if this tab has a token to share
      const token = authStorage.getToken()

      if (token) {
        try {
          channel.postMessage({
            type: MESSAGE_TYPES.AUTH_RESPONSE,
            token: token,
            timestamp: Date.now(),
          })
        } catch (error) {
          console.error(
            "[CrossTabAuthBridge] Failed to send auth response:",
            error
          )
        }
      }
    }
  }

  // Return cleanup function
  return {
    stop: () => {
      try {
        channel.close()
      } catch (error) {
        console.warn("[CrossTabAuthBridge] Error closing channel:", error)
      }
    },
  }
}

export default {
  requestAuthTokenFromOtherTab,
  listenForAuthRequests,
}
